import React, { useState, useEffect } from 'react';
import './App.css';
import { ethers } from 'ethers';

// Import contract components
import UserManager from './components/UserManager';
import CollectionManager from './components/CollectionManager';
import FactoryManager from './components/FactoryManager';
import { useDeploymentInfo } from './hooks/useDeploymentInfo';
import { useFactoryAddresses } from './hooks/useFactoryAddresses';

function App() {
  const [currentView, setCurrentView] = useState<'user-manager' | 'collection-manager' | 'factory-manager'>('factory-manager');
  const [deployedContracts, setDeployedContracts] = useState<{
    collections: Array<{ address: string; type: 'open' | 'closed'; name: string }>;
    sales: Array<{ address: string; type: 'direct' | 'auction' | 'direct-piece' | 'auction-piece'; collectionAddress: string }>;
  }>({
    collections: [],
    sales: []
  });

  // Wallet connection state
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [currentChainId, setCurrentChainId] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [walletError, setWalletError] = useState<string | null>(null);

  const { deploymentInfo, loading, error } = useDeploymentInfo();
  const { factoryAddresses } = useFactoryAddresses();

  // Network configuration
  const TARGET_CHAIN_ID = factoryAddresses?.chainId || 8453; // Default to Base if not loaded
  const ANVIL_CHAIN_ID = 31337; // Anvil default chain ID
  const TARGET_RPC_URL = factoryAddresses?.rpcUrl || "https://mainnet.base.org";

  // Check wallet connection
  const checkWalletConnection = async () => {
    if (!window.ethereum) {
      setWalletError('MetaMask is not installed. Please install MetaMask to use this application.');
      return false;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const network = await provider.getNetwork();
        const signer = await provider.getSigner();
        const address = await signer.getAddress();

        setWalletConnected(true);
        setWalletAddress(address);
        setCurrentChainId(Number(network.chainId));
        setWalletError(null);
        return true;
      } else {
        setWalletConnected(false);
        setWalletAddress(null);
        setCurrentChainId(null);
        setWalletError(null);
        return false;
      }
    } catch (err) {
      setWalletError('Failed to check wallet connection.');
      return false;
    }
  };

  // Connect wallet
  const connectWallet = async () => {
    if (!window.ethereum) {
      setWalletError('MetaMask is not installed. Please install MetaMask to use this application.');
      return;
    }

    setIsConnecting(true);
    setWalletError(null);

    try {
      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      setWalletConnected(true);
      setWalletAddress(address);
      setCurrentChainId(Number(network.chainId));

      // Check if we need to switch to Target network (only if not on Anvil/localhost)
      if (Number(network.chainId) !== TARGET_CHAIN_ID && Number(network.chainId) !== ANVIL_CHAIN_ID) {
        await switchToTargetNetwork();
      }
    } catch (err) {
      setWalletError(err instanceof Error ? err.message : 'Failed to connect wallet.');
    } finally {
      setIsConnecting(false);
    }
  };

  // Switch to Target network
  const switchToTargetNetwork = async () => {
    if (!window.ethereum) return;
    
    // Don't try to switch if we don't have the chain ID loaded yet
    if (!factoryAddresses?.chainId && TARGET_CHAIN_ID === 8453) {
      // Allow fallback to Base hardcoded
    } else if (!factoryAddresses?.chainId) {
      return; 
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${TARGET_CHAIN_ID.toString(16)}` }],
      });
    } catch (switchError: any) {
      // If the network doesn't exist, add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${TARGET_CHAIN_ID.toString(16)}`,
              chainName: factoryAddresses?.network || 'Target Network',
              nativeCurrency: {
                name: 'Ethereum',
                symbol: 'ETH',
                decimals: 18,
              },
              rpcUrls: [TARGET_RPC_URL],
              blockExplorerUrls: [factoryAddresses?.chainId === 1 ? 'https://etherscan.io' : 'https://basescan.org'],
            }],
          });
        } catch (addError) {
          setWalletError(`Failed to add ${factoryAddresses?.network || 'target'} network to wallet.`);
        }
      } else {
        setWalletError(`Failed to switch to ${factoryAddresses?.network || 'target'} network.`);
      }
    }
  };

  // Check wallet connection on mount
  useEffect(() => {
    checkWalletConnection();
  }, []);

  // Listen for account and chain changes
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          setWalletConnected(false);
          setWalletAddress(null);
          setCurrentChainId(null);
        } else {
          checkWalletConnection();
        }
      };

      const handleChainChanged = () => {
        checkWalletConnection();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum?.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  // Pre-populate contracts from deployment info
  useEffect(() => {
    if (deploymentInfo) {
      // Use the dynamically loaded collections and sales from the hook
      setDeployedContracts({ 
        collections: deploymentInfo.deployedCollections, 
        sales: deploymentInfo.deployedSales 
      });
    }
  }, [deploymentInfo]);

  const addDeployedCollection = (address: string, type: 'open' | 'closed', name: string) => {
    setDeployedContracts(prev => ({
      ...prev,
      collections: [...prev.collections, { address, type, name }]
    }));
  };

  const addDeployedSale = (address: string, type: 'direct' | 'auction' | 'direct-piece' | 'auction-piece', collectionAddress: string) => {
    setDeployedContracts(prev => ({
      ...prev,
      sales: [...prev.sales, { address, type, collectionAddress }]
    }));
  };

  // Show wallet connection screen if not connected
  if (!walletConnected) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Fakewhale Alpha Management</h1>
          <p>Connect your wallet to interact with Fakewhale smart contracts</p>
          
          <div className="wallet-connection">
            {!window.ethereum ? (
              <div className="error-message">
                <p>❌ MetaMask is not installed</p>
                <p>Please install MetaMask to use this application.</p>
                <a 
                  href="https://metamask.io/download/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="install-link"
                >
                  Install MetaMask
                </a>
              </div>
            ) : (
              <div className="connect-section">
                <button 
                  onClick={connectWallet}
                  disabled={isConnecting}
                  className="connect-button"
                >
                  {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                </button>
                {walletError && (
                  <div className="error-message">
                    <p>❌ {walletError}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Fakewhale Alpha Management</h1>
          <p>Loading deployment information...</p>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Fakewhale Alpha Management</h1>
          <p>Error loading deployment info: {error}</p>
          <p>Please make sure Anvil is running and contracts are deployed.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Fakewhale Alpha Management</h1>
        
        <div className="wallet-info">
          <div className="wallet-status">
            <p>🔗 Wallet: {walletAddress}</p>
            <p>🌐 Network: {deploymentInfo?.network || (currentChainId === ANVIL_CHAIN_ID ? 'localhost' : 'base')} (Chain ID: {currentChainId || deploymentInfo?.chainId})</p>
            {currentChainId !== (factoryAddresses?.chainId || 8453) && currentChainId !== ANVIL_CHAIN_ID && (
              <div className="network-warning">
                <p>⚠️ Please switch to {factoryAddresses?.network || 'Base'} network or connect to localhost (Anvil)</p>
                <button onClick={switchToTargetNetwork} className="switch-network-button">
                  Switch to {factoryAddresses?.network || 'Base'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="navigation">
        <button 
          className={currentView === 'user-manager' ? 'active' : ''}
          onClick={() => setCurrentView('user-manager')}
        >
          User Manager
        </button>
        <button 
          className={currentView === 'collection-manager' ? 'active' : ''}
          onClick={() => setCurrentView('collection-manager')}
        >
          Collection Manager
        </button>
        <button 
          className={currentView === 'factory-manager' ? 'active' : ''}
          onClick={() => setCurrentView('factory-manager')}
        >
          Factory Manager
        </button>
      </nav>

      <main className="main-content">
        {currentView === 'user-manager' && (
          <>
            <h2>👥 User Manager</h2>
            <p>Manage users who are allowed to create contracts through the factories.</p>
            <UserManager />
          </>
        )}
        
        {currentView === 'collection-manager' && (
          <>
            <h2>📋 Collection Manager</h2>
            <p>Manage collection settings and view token metadata.</p>
            <CollectionManager 
              collections={deployedContracts.collections}
            />
          </>
        )}
        
        {currentView === 'factory-manager' && (
          <FactoryManager />
        )}
      </main>
    </div>
  );
}

export default App;